<template>
  <AppLayout>
    <div class="max-w-4xl mx-auto p-4 pb-16 space-y-6">

      <!-- ── Back link ─────────────────────────────────────────────────────── -->
      <router-link to="/user?tab=groups"
        class="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Back to Groups
      </router-link>

      <!-- ── Loading ────────────────────────────────────────────────────────── -->
      <div v-if="isLoading && !currentGroup" class="flex flex-col items-center justify-center py-24 gap-4">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
        <p class="text-sm text-gray-400">Loading group…</p>
      </div>

      <!-- ── Error ──────────────────────────────────────────────────────────── -->
      <div v-else-if="error && !currentGroup"
        class="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
        <svg class="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd" />
        </svg>
        <div>
          <p class="font-medium text-red-800">{{ error }}</p>
          <button @click="retry" class="mt-2 text-sm text-red-600 hover:text-red-800">Try again</button>
        </div>
      </div>

      <template v-else-if="currentGroup">

        <!-- ── Page header ────────────────────────────────────────────────── -->
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div class="space-y-2">
            <!-- Name + badges -->
            <div class="flex items-center gap-3 flex-wrap">
              <h1 class="text-2xl font-bold text-gray-900">{{ currentGroup.name }}</h1>
              <span v-if="currentGroup.is_personal"
                class="px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                Personal
              </span>
              <span v-if="myRole" :class="roleBadgeClass(myRole)"
                class="px-2.5 py-0.5 text-xs font-medium rounded-full">
                You: {{ myRole }}
              </span>
            </div>
            <!-- External ID -->
            <p class="text-xs text-gray-400 font-mono tracking-wide">
              ID: {{ currentGroup.external_id }}
            </p>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-2 flex-shrink-0">
            <BaseButton v-if="canManageGroup" @click="editGroup" variant="secondary" size="md">
              <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Group
            </BaseButton>
            <BaseButton v-if="canDeleteGroup" @click="showDeleteDialog = true" variant="danger" size="md">
              <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </BaseButton>
          </div>
        </div>

        <!-- ── Stats row ──────────────────────────────────────────────────── -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <!-- Members -->
          <div class="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-gray-900">{{ members.length }}</p>
              <p class="text-xs text-gray-500">Members</p>
            </div>
          </div>

          <!-- Owner -->
          <div class="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ ownerName }}</p>
              <p class="text-xs text-gray-500">Owner</p>
            </div>
          </div>

          <!-- Created -->
          <div class="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">{{ formatDate(currentGroup.created_at) }}</p>
              <p class="text-xs text-gray-500">Created</p>
            </div>
          </div>

          <!-- Last updated -->
          <div class="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">{{ formatDate(currentGroup.updated_at) }}</p>
              <p class="text-xs text-gray-500">Last updated</p>
            </div>
          </div>
        </div>

        <!-- ── Description ─────────────────────────────────────────────────── -->
        <div class="bg-white border border-gray-200 rounded-xl p-6">
          <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Description</h2>
          <p v-if="currentGroup.description" class="text-gray-700 leading-relaxed">
            {{ currentGroup.description }}
          </p>
          <p v-else class="text-gray-400 italic text-sm">No description provided.</p>
        </div>

        <!-- ── Members ─────────────────────────────────────────────────────── -->
        <div class="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <!-- Section header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h2 class="font-semibold text-gray-900">
                Members
                <span class="ml-1.5 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  {{ members.length }}
                </span>
              </h2>
              <p class="text-xs text-gray-400 mt-0.5">People with access to this group</p>
            </div>
            <BaseButton v-if="canAddMembers" @click="showAddMemberDialog = true" variant="primary" size="sm">
              <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Invite Member
            </BaseButton>
          </div>

          <!-- Members list -->
          <div class="p-4">
            <GroupMembersList :members="Array.from(members)" :can-manage="canManageGroup" :group-id="String(groupId)"
              :owner-id="currentGroup.owner_id" :loading="isMembersLoading" :error="membersError"
              @remove-member="handleRemoveMember" @update-role="handleUpdateRole" @leave-group="handleLeaveGroup"
              @retry="loadMembers" />
          </div>
        </div>

      </template>
    </div>

    <!-- ── Dialogs ─────────────────────────────────────────────────────────── -->
    <GroupDeleteDialog :is-open="showDeleteDialog" :group-name="currentGroup?.name || ''" :group-id="groupId"
      @confirm="handleDeleteConfirm" @cancel="showDeleteDialog = false" @close="showDeleteDialog = false" />

    <InviteMemberDialog :is-open="showAddMemberDialog" :group-id="groupId" :existing-member-ids="existingMemberIds"
      @invited="handleInviteSent" @close="showAddMemberDialog = false" />
  </AppLayout>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useGroupsStore } from '../../stores/groups'
import { useAuthStore } from '../../stores/auth'
import { storeToRefs } from 'pinia'
import AppLayout from '../../components/layout/AppLayout.vue'
import BaseButton from '../../components/BaseButton.vue'
import GroupDeleteDialog from '../../components/groups/GroupDeleteDialog.vue'
import GroupMembersList from '../../components/groups/GroupMembersList.vue'
import InviteMemberDialog from '../../components/groups/InviteMemberDialog.vue'
import { useToast } from '../../composables/useToast'
import { memberDisplayName, type GroupMember } from '../../types/group'

const router = useRouter()
const groupsStore = useGroupsStore()
const authStore = useAuthStore()
const toast = useToast()

const props = defineProps<{ id: string }>()

const { currentGroup, members, isLoading, error, canManageGroup, canDeleteGroup } = storeToRefs(groupsStore)

const groupId = computed(() => parseInt(props.id))
const showDeleteDialog = ref(false)
const showAddMemberDialog = ref(false)
const isMembersLoading = ref(false)
const membersError = ref<string | null>(null)

const canAddMembers = computed(() => canManageGroup.value)
const existingMemberIds = computed(() => members.value.map(m => m.user_id))

// Owner is identified by group.owner_id, not by a role value
const ownerMember = computed(() =>
  currentGroup.value
    ? members.value.find(m => m.user_id === currentGroup.value!.owner_id)
    : null
)
const ownerName = computed(() =>
  ownerMember.value ? memberDisplayName(ownerMember.value) : '—'
)

// Current authenticated user's role in this group
const myRole = computed(() => {
  const uid = authStore.user?.id
  if (!uid || !currentGroup.value) return null
  if (currentGroup.value.owner_id === uid) return 'owner'
  return members.value.find(m => m.user_id === uid)?.role ?? null
})

function roleBadgeClass(role: string) {
  switch (role) {
    case 'owner': return 'bg-purple-100 text-purple-700'
    case 'admin': return 'bg-blue-100 text-blue-700'
    default: return 'bg-gray-100 text-gray-600'
  }
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  })
}

// ── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  await loadGroup()
  await loadMembers()
})

async function loadGroup() {
  try { await groupsStore.fetchGroup(groupId.value) }
  catch (err) { console.error('Failed to load group:', err) }
}

async function loadMembers() {
  isMembersLoading.value = true
  membersError.value = null
  try { await groupsStore.fetchMembers(groupId.value) }
  catch { membersError.value = 'Failed to load members. Please try again.' }
  finally { isMembersLoading.value = false }
}

function retry() { loadGroup(); loadMembers() }

// ── Actions ──────────────────────────────────────────────────────────────────
function editGroup() { router.push(`/groups/${groupId.value}/edit`) }

async function handleDeleteConfirm() {
  try {
    await groupsStore.deleteGroup(groupId.value)
    toast.success('Group deleted', 'The group has been successfully deleted.')
    router.push('/user?tab=groups')
  } catch {
    toast.error('Delete failed', 'Failed to delete the group. Please try again.')
  } finally {
    showDeleteDialog.value = false
  }
}

async function handleRemoveMember(member: GroupMember) {
  try {
    await groupsStore.removeMember(groupId.value, member.user_id)
    toast.success('Member removed', `${memberDisplayName(member)} has been removed.`)
    await loadMembers()
  } catch {
    toast.error('Remove failed', 'Failed to remove the member. Please try again.')
  }
}

async function handleUpdateRole(member: GroupMember, newRole: string) {
  try {
    await groupsStore.updateMemberRole(groupId.value, member.user_id, newRole)
    toast.success('Role updated', `Role updated to ${newRole}.`)
    await loadMembers()
  } catch {
    toast.error('Update failed', 'Failed to update member role. Please try again.')
  }
}

async function handleLeaveGroup() {
  try {
    await groupsStore.leaveGroup(groupId.value)
    toast.success('Left group', 'You have successfully left the group.')
    router.push('/user?tab=groups')
  } catch {
    toast.error('Leave failed', 'Failed to leave the group. Please try again.')
  }
}

function handleInviteSent() {
  toast.success('Invitation sent', 'The user will receive a notification to join.')
}
</script>
